import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { SfDataService } from './sf-data.service';
import { AccountService } from './account.service';

export interface HubReportContext {
  hubNumber?: string;
  currentWeekEnding?: string;   // yyyy-MM-dd
  missingTemplate?: boolean;    // true => this week's report is still due
  weekOptions?: string[];
}

/**
 * Feature 9 — shared "weekly report due" status for the logged-in hub. Backs the menu badge,
 * the dashboard reminder, and the upload screen's week dropdown, and updates them live after
 * a successful upload (call refresh()).
 */
@Injectable({ providedIn: 'root' })
export class HubReportStatusService {

  private readonly context$ = new BehaviorSubject<HubReportContext | null>(null);
  private readonly due$ = new BehaviorSubject<boolean>(false);

  constructor(private sfDataService: SfDataService, private accountService: AccountService) { }

  /** True when the current week's report has not been submitted (drives the reminder UI). */
  get reportDue(): Observable<boolean> { return this.due$.asObservable(); }

  /**
   * True once the report is due AND we're on day 4 (Thursday) or later of the report week —
   * escalates the reminder from a routine "yellow" nudge to an urgent "red/flashing" one on the
   * menu badge, dashboard banner and upload page banner.
   */
  get reportUrgent(): Observable<boolean> {
    return this.due$.pipe(map(due => due && HubReportStatusService.isDayFourOrLater()));
  }

  /** Monday = day 1 ... Sunday = day 7, so Thursday is day 4. */
  private static isDayFourOrLater(): boolean {
    const today = new Date().getDay(); // 0 = Sunday ... 6 = Saturday
    const mondayIndexed = today === 0 ? 7 : today;
    return mondayIndexed >= 4;
  }

  /** The last-loaded context (week options, current week, etc.). */
  get context(): Observable<HubReportContext | null> { return this.context$.asObservable(); }

  get snapshot(): HubReportContext | null { return this.context$.value; }

  isHubUser(): boolean {
    const roles = this.accountService.currentUser?.roles || [];
    return roles.includes('Hub') || roles.includes('HubAssistant');
  }

  /** Re-fetch the hub's report status. No-op (clears state) for non-hub users. */
  refresh(): void {
    if (!this.isHubUser()) {
      this.context$.next(null);
      this.due$.next(false);
      return;
    }
    this.sfDataService.getHubReportContext().subscribe({
      next: (raw: any) => {
        const ctx = this.normalize(raw);
        this.context$.next(ctx);
        this.due$.next(!!ctx.missingTemplate);
      },
      error: () => {
        // Fail soft — a failed status check should never break the menu or dashboard.
        this.context$.next(null);
        this.due$.next(false);
      }
    });
  }

  private normalize(r: any): HubReportContext {
    if (!r) { return {}; }
    return {
      hubNumber: r.hubNumber ?? r.HubNumber,
      currentWeekEnding: r.currentWeekEnding ?? r.CurrentWeekEnding,
      missingTemplate: r.missingTemplate ?? r.MissingTemplate ?? false,
      weekOptions: r.weekOptions ?? r.WeekOptions ?? []
    };
  }
}
